import { useEffect, useMemo, useState, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { Select } from "./Select";

export interface Column<T> {
  /** Stable key; also the default sort accessor when `sortValue` is omitted. */
  key: string;
  header: ReactNode;
  /** Cell renderer. `index` / `shownRows` are the row's position in, and the rows of, the page AS
   *  RENDERED (after sort, pin and pagination) — for a cell that depends on the row printed directly
   *  above it (the Subscription tab's repeated-product rule). Most cells ignore both. */
  cell: (row: T, index: number, shownRows: readonly T[]) => ReactNode;
  /** Sortable when provided; returns the comparable value for a row. */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: "left" | "right";
  /** Use tabular/mono figures (numbers, IDs, money). */
  numeric?: boolean;
  className?: string;
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Accessible caption for screen readers. */
  caption: string;
  onRowClick?: (row: T) => void;
  /** Default sort: column key + direction. */
  initialSort?: { key: string; dir: "asc" | "desc" };
  className?: string;
  empty?: ReactNode;
  /** Optional per-row extra classes (e.g. highlight a deep-linked row). */
  rowClassName?: (row: T) => string | undefined;
  /** Optional per-row ref (e.g. to scroll a deep-linked row into view). Return undefined to skip. */
  rowRef?: (row: T) => RefObject<HTMLTableRowElement | null> | undefined;
  /** Paginate + show the result count (default true). Set false for tiny fixed tables. */
  paginated?: boolean;
  /** Initial page size (default 15). */
  initialPageSize?: number;
  /** Rows for which this returns true are kept pinned to the top, above any sort (e.g. a base plan). */
  pinFirst?: (row: T) => boolean;
  /** rev 17b — fires with the CURRENT page's rows (`pagedRows`) whenever they change (mount, sort,
   *  page, or page-size). The only seam a caller needs to build a page-scoped "select all" (Pool Users'
   *  bulk bar) without re-deriving Table's own sort/paginate math — see rev17b-fe-design.md §2. */
  onVisibleRowsChange?: (rows: T[]) => void;
}

type SortState = { key: string; dir: "asc" | "desc" } | null;

const PAGE_SIZES = [15, 50, 100];

/**
 * Page-number list with ellipses for long ranges, e.g. [1,2,3,4,5,6,"…",17] at page 1 or
 * [1,"…",7,8,9,"…",17] in the middle. First + last are always shown; a window of ±2 around the current
 * page; and the near-edge is padded to a fuller run (1..6 / total-5..total) so the first/last pages read
 * as a solid block rather than "1 2 … 17". Exported for unit testing (this windowing is easy to get
 * subtly wrong at the boundaries).
 */
export function pageList(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const want = new Set([1, total, current - 2, current - 1, current, current + 1, current + 2]);
  if (current <= 4) for (let p = 2; p <= 6; p++) want.add(p);
  if (current >= total - 3) for (let p = total - 5; p <= total - 1; p++) want.add(p);
  const out: (number | "…")[] = [];
  let prev = 0;
  for (let p = 1; p <= total; p++) {
    if (!want.has(p) || p < 1 || p > total) continue;
    if (p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

/**
 * Dense, sortable, accessible data table with client-side pagination. Sorting is stable; aria-sort is
 * set on the active header; clickable rows are keyboard-operable (Enter AND Space). The paginator +
 * result count live BELOW the horizontal-scroll region so the page-size menu never clips.
 */
export function Table<T>({
  columns,
  rows,
  rowKey,
  caption,
  onRowClick,
  initialSort,
  className,
  empty,
  rowClassName,
  rowRef,
  paginated = true,
  initialPageSize = 15,
  pinFirst,
  onVisibleRowsChange,
}: TableProps<T>) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState>(initialSort ?? null);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(1);

  const sortedRows = useMemo(() => {
    let out = rows;
    const col = sort ? columns.find((c) => c.key === sort.key) : undefined;
    if (sort && col?.sortValue) {
      const accessor = col.sortValue;
      const factor = sort.dir === "asc" ? 1 : -1;
      out = [...rows].sort((a, b) => {
        const va = accessor(a);
        const vb = accessor(b);
        if (va == null && vb == null) return 0;
        if (va == null) return 1; // nulls last regardless of direction
        if (vb == null) return -1;
        if (typeof va === "number" && typeof vb === "number") return (va - vb) * factor;
        return String(va).localeCompare(String(vb)) * factor;
      });
    }
    if (pinFirst) {
      // Stable partition: pinned rows (e.g. the base plan) stay on top regardless of the active sort.
      out = [...out].sort((a, b) => Number(Boolean(pinFirst(b))) - Number(Boolean(pinFirst(a))));
    }
    return out;
  }, [rows, sort, columns, pinFirst]);

  const total = sortedRows.length;
  const pageCount = paginated ? Math.max(1, Math.ceil(total / pageSize)) : 1;

  // Keep the current page valid as rows / filters / page-size change (e.g. a new sort, a smaller set).
  useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);

  const pagedRows = useMemo(
    () => (paginated ? sortedRows.slice((page - 1) * pageSize, page * pageSize) : sortedRows),
    [sortedRows, paginated, page, pageSize],
  );

  // Fire on pagedRows identity only — deliberately NOT depending on onVisibleRowsChange itself: the
  // caller's own callback is expected to be referentially unstable (an inline closure over per-render
  // state, e.g. PoolUsersPage's own handler), and depending on it would fire this effect every render
  // for no actual row change.
  useEffect(() => {
    onVisibleRowsChange?.(pagedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagedRows]);

  function toggleSort(key: string) {
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null; // third click clears the sort
    });
  }

  if (rows.length === 0 && empty) return <>{empty}</>;

  return (
    <div className={cn("flex flex-col gap-2.5", className)}>
      {/* The scroll region is BOTH axes + height-capped so a long table scrolls inside the card and the
          sticky header (thead th: sticky top-0) stays pinned. Short tables never reach the cap → no scroll. */}
      <div className="max-h-[68vh] overflow-auto rounded-(--radius) border border-(--border) bg-(--surface) shadow-(--shadow-sm)">
        <table className="w-full border-collapse text-(length:--text-sm)">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-(--border) bg-(--surface-sunken)">
              {columns.map((col) => {
                const sortable = Boolean(col.sortValue);
                const active = sort?.key === col.key;
                const ariaSort = active
                  ? sort.dir === "asc"
                    ? "ascending"
                    : "descending"
                  : "none";
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={sortable ? ariaSort : undefined}
                    className={cn(
                      "sticky top-0 z-[1] whitespace-nowrap bg-(--surface-sunken) px-4 py-2.5 text-(length:--text-xs) font-semibold text-(--text-muted)",
                      col.align === "right" ? "text-right" : "text-left",
                    )}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        title={
                          active && sort.dir === "asc" ? t("action.sortDesc") : t("action.sortAsc")
                        }
                        className={cn(
                          "inline-flex items-center gap-1 hover:text-(--text) " +
                            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand) " +
                            "rounded-(--radius-sm)",
                          col.align === "right" && "flex-row-reverse",
                        )}
                      >
                        {col.header}
                        {active ? (
                          sort.dir === "asc" ? (
                            <ArrowUp className="size-3" aria-hidden />
                          ) : (
                            <ArrowDown className="size-3" aria-hidden />
                          )
                        ) : (
                          <ChevronsUpDown className="size-3 opacity-50" aria-hidden />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, rowIndex) => (
              <tr
                key={rowKey(row)}
                ref={rowRef?.(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                className={cn(
                  "border-b border-(--border-hairline) last:border-0 transition-colors duration-(--dur-fast)",
                  onRowClick &&
                    "cursor-pointer hover:bg-(--surface-hover) focus-visible:outline-2 " +
                      "focus-visible:outline-offset-[-2px] focus-visible:outline-(--brand)",
                  rowClassName?.(row),
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      "px-4 py-2.5 text-(--text)",
                      col.align === "right" ? "text-right" : "text-left",
                      col.numeric && "tabular",
                      col.className,
                    )}
                  >
                    {col.cell(row, rowIndex, pagedRows)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginated && (
        // Reference layout: page numbers LEFT, "15 per page" MIDDLE, "N result(s)" pushed RIGHT.
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5 px-0.5 text-(length:--text-xs) text-(--text-muted)">
          {pageCount > 1 && (
            <nav className="flex items-center gap-1.5" aria-label={t("table.pagination")}>
              <PageBtn
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                label={t("table.prevPage")}
              >
                <ChevronLeft className="size-3.5" aria-hidden />
              </PageBtn>
              {pageList(page, pageCount).map((p, i) =>
                p === "…" ? (
                  <span key={`e${i}`} className="px-1 text-(--text-subtle)" aria-hidden>
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPage(p)}
                    aria-current={p === page ? "page" : undefined}
                    aria-label={t("table.goToPage", { n: p })}
                    className={cn(
                      "tabular grid h-7 min-w-7 place-items-center rounded-(--radius-sm) px-1.5 text-(length:--text-xs) transition-colors",
                      p === page
                        ? "bg-(--brand) font-semibold text-(--text-onbrand)"
                        : "text-(--text-muted) hover:bg-(--surface-hover) hover:text-(--text)",
                      "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(--brand)",
                    )}
                  >
                    {p}
                  </button>
                ),
              )}
              <PageBtn
                disabled={page >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                label={t("table.nextPage")}
              >
                <ChevronRight className="size-3.5" aria-hidden />
              </PageBtn>
            </nav>
          )}

          <label className="flex items-center gap-2 text-(length:--text-2xs) font-medium text-(--text-subtle)">
            <span className="sr-only">{t("table.perPage")}</span>
            <Select
              value={String(pageSize)}
              onChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
              options={PAGE_SIZES.map((n) => ({
                value: String(n),
                label: t("table.perPageN", { count: n }),
              }))}
              size="sm"
              aria-label={t("table.perPage")}
            />
          </label>

          <span className="tabular ml-auto">{t("table.results", { count: total })}</span>
        </div>
      )}
    </div>
  );
}

function PageBtn({
  disabled,
  onClick,
  label,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="grid size-7 place-items-center rounded-(--radius-sm) text-(--text-muted) transition-colors hover:bg-(--surface-hover) hover:text-(--text) disabled:opacity-40 disabled:hover:bg-transparent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(--brand)"
    >
      {children}
    </button>
  );
}
